import { inject } from '@angular/core';
import { CanActivateFn, CanMatchFn, Router } from '@angular/router';

import { AdminPortal, AuthService } from './auth.service';

/** Blocks access to app routes unless the user is signed in. */
export const authGuard: CanActivateFn = () => {
  const auth = inject(AuthService);
  const router = inject(Router);

  return auth.isAuthenticated() ? true : router.createUrlTree(['/login']);
};

/** Keeps signed-in users away from the login page. */
export const publicOnlyGuard: CanActivateFn = () => {
  const auth = inject(AuthService);
  const router = inject(Router);

  return auth.isAuthenticated() ? router.createUrlTree(['/dashboard']) : true;
};

/**
 * Admin app: picks which of the two sibling shells (SuperAdmin / clinic-owner
 * Admin) handles a URL. Both portals share the same paths (/dashboard, …), so
 * a non-matching portal returns false and the router tries the next sibling.
 * A session that belongs to neither portal (e.g. a stale CRM session on the
 * same origin) is signed out instead of being shown an admin shell.
 */
export function portalMatch(portal: AdminPortal): CanMatchFn {
  return async () => {
    const auth = inject(AuthService);
    const router = inject(Router);

    if (!auth.isAuthenticated()) {
      return router.createUrlTree(['/login']);
    }

    const current = auth.adminPortal();
    if (current === portal) {
      return true;
    }
    if (current !== null) {
      return false;
    }

    await auth.logout();
    return router.createUrlTree(['/login']);
  };
}
